import { useEffect, useState } from 'react';

/**
 * Wraps a boolean `isLoading` so it only returns true AFTER a minimum
 * delay. This prevents the jarring "Loading…" text flash on fast
 * page-to-page navigation — most Supabase queries on a decent
 * connection return inside 200–300ms, well below the threshold at
 * which a human perceives any wait.
 *
 * Pattern in a page component:
 *
 *   const [loading, setLoading] = useState(true);
 *   const showLoading = useDelayedLoading(loading);
 *   // …
 *   if (loading) {
 *     return (
 *       <AppShell title="…">
 *         {showLoading && <p className="muted">Loading…</p>}
 *       </AppShell>
 *     );
 *   }
 *
 * Result on fast loads (under the delay): the page chrome (header, back
 * button, bottom tab bar) pops in instantly, the content area is
 * briefly empty, then the real content renders. Feels instant.
 *
 * Result on slow loads (> delay): the same chrome appears, then
 * "Loading…" fades in after the delay, then content renders. Feels
 * responsive rather than broken.
 *
 * As soon as `isLoading` flips to false, this returns false on the
 * same tick — there's no "minimum show time" because guaranteed-show
 * spinners feel laggy too.
 */
export function useDelayedLoading(isLoading: boolean, delayMs = 1200): boolean {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setShouldShow(false);
      return;
    }
    const t = window.setTimeout(() => setShouldShow(true), delayMs);
    return () => window.clearTimeout(t);
  }, [isLoading, delayMs]);

  return shouldShow;
}
